'use client'
import React, { useState, useEffect } from 'react';
import { Box, Button, Heading, Input, Stack, useToast, Avatar, Text, Progress, Flex, useColorModeValue } from '@chakra-ui/react';
import Select from "react-select";
import axios from "axios"
import { setToken, setRefreshToken, setUserType, setAccountId } from '../utils/authService';
import { useNavigate, useLocation } from 'react-router-dom';



const steps = [
	{ id: 1, label: 'Basic Information', description: 'Enter your name and number' },
	{ id: 2, label: 'Login Details', description: 'Create your username and password' },
	{ id: 3, label: 'Visual Creator?', description: 'Are you a creative professional, like a photographer/videographer, content creator, or studio host?' },
	{ id: 4, label: 'Location', description: 'Where can clients find you?' },
	{ id: 5, label: 'Profile Setup', description: 'Upload your profile picture' },
];


const options = [
	{ value: "Street", label: "Street" },
	{ value: "Boudoir", label: "Boudoir" },
	{ value: "Weddings", label: "Weddings" },
	{ value: "Events", label: "Events" },
	{ value: "Editorial", label: "Editorial" },
	{ value: "Last Minute", label: "Last Minute" },
	{ value: "Social Media", label: "Social Media" },
	{ value: "Real Estate", label: "Real Estate" },
];



const Onboarding = ({ setIsLoggedIn }) => {
	const bgColor = useColorModeValue('white', 'black');
	const dropdownTextColor = useColorModeValue('white', 'black');
	const boxColor = useColorModeValue('white', 'black');
	const placeholderColor = 'gray.500'
	const toast = useToast();
	const navigate = useNavigate();
	const [isVisualCreator, setIsVisualCreator] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const location = useLocation();

	// Pull referral code from URL (e.g., ?ref=jdoe45)
	useEffect(() => {
		const queryParams = new URLSearchParams(location.search);
		const ref = queryParams.get("ref");
		if (ref) {
			setFormData(prev => ({ ...prev, ref }));
		}
	}, [location.search]);



	const [currentStep, setCurrentStep] = useState(0);
	const [formData, setFormData] = useState({
		first_name: '',
		last_name: '',
		email: '',
		number: '',
		username: '',
		password: '',
		user_type: '',
		street_address: '',
		city: '',
		state: '',
		zip: '',
		country: 'US', // We are only working with US now
		camera: '',
		equipment: '',
		photography_style: '',
		profilePicture: null,
		fileName: '',
		fileType: '',
		ref: ''
	});

	// Validation function
	const isNextDisabled = () => {
		switch (currentStep) {
			case 0: // Basic Information
				return (
					!formData.first_name.trim() ||
					!formData.last_name.trim() ||
					!formData.number.trim()
				);
			case 1: // Login Details
				return (
					!formData.email.trim() || !formData.password.trim()
				);
			case 2: // Visual Creator
				if (formData.user_type === "photographer" || formData.user_type === "videographer") {
					// Photographer, Videographer,  & Studio must provide equipment and photography_style
					return (
						!formData.equipment.trim() ||
						!formData.photography_style.trim()
					);
				}
				else if (formData.user_type === "studio") {
					// Model must provide only photography_style
					return (
						!formData.equipment.trim()
					);
				}
				else if (formData.user_type === "model") {
					// Model must provide only photography_style
					return (
						!formData.photography_style.trim()
					);
				}
				// Customer just needs user_type selected
				return !formData.user_type;

			case 3: // Location Step
				return (
					!formData.street_address?.trim() ||
					!formData.city?.trim() ||
					!formData.state?.trim() ||
					!formData.zip?.trim()
				);
			case 4: // Profile Setup
				return !formData.profilePicture; // Ensure a profile picture is uploaded
			default:
				return false;
		}
	};

	const handleNext = () => {
		const requiredFields = [
			"street_address",
			"city",
			"state",
			"zip",
		];

		if (currentStep === 3) {
			const isEmpty = requiredFields.some((field) => !formData[field]?.trim());
			if (isEmpty) {
				alert("Please fill in all the location fields.");
				return;
			}
		}

		if (currentStep < steps.length - 1) {
			setCurrentStep(currentStep + 1);
		} else {
			alert("Onboarding Complete!");
		}
	};

	const handleBack = () => {
		if (currentStep > 0) {
			setCurrentStep(currentStep - 1);
		}
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	const handleSubmit = async () => {
		setIsSubmitting(true);

		try {
			const fullAddress = `${formData.street_address}, ${formData.city}, ${formData.state}, ${formData.zip}, ${formData.country}`;

			// Step 1: Geocode first
			const { data: geoData } = await axios.get('https://maps.googleapis.com/maps/api/geocode/json', {
				params: { address: fullAddress, key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY }
			});

			const location = geoData.results[0]?.geometry?.location;
			if (!location) throw new Error('Invalid address provided.');

			const updatedFormData = { ...formData, latitude: location.lat, longitude: location.lng };

			const backendUrl = process.env.NODE_ENV === 'production'
				? process.env.REACT_APP_BACKEND_URL
				: 'http://localhost:8080';

			// Step 2: Signup
			const signupRes = await axios.post(`${backendUrl}/signup`, updatedFormData, {
				headers: { 'Content-Type': 'application/json' }
			});

			const user = signupRes.data;

			// Step 3: Upload avatar (if any)
			if (formData.profilePicture && user?.user?.avatarBlob?.uploadUrl) {
				await axios.put(user.user.avatarBlob.uploadUrl, formData.profilePicture, {
					headers: { 'Content-Type': formData.fileType }
				});
			}

			// Step 4: Auto-login
			const { data: loginRes } = await axios.post(`${backendUrl}/login`, {
				email: updatedFormData.email,
				password: updatedFormData.password
			});

			const { token, refresh_token, account_id, user_type } = loginRes;

			setToken(token);
			setRefreshToken(refresh_token);
			setIsLoggedIn(true);
			setUserType(user_type);
			setAccountId(account_id);

			toast({
				title: 'Signup successful!',
				status: 'success',
				duration: 3000,
				isClosable: true,
			});

			// Slight delay before navigating to ensure app state updates
			setTimeout(() => {
				navigate('/search');
			}, 400);

		} catch (error) {
			console.error(error);
			toast({
				title: 'Signup failed.',
				description: error.message || 'Please try again.',
				status: 'error',
				duration: 4000,
				isClosable: true,
			});
		} finally {
			setIsSubmitting(false);
		}
	};



	const renderStep = () => {
		switch (currentStep) {
			case 0:
				return (
					<Stack spacing={4}>
						<Input
							placeholder="First Name"
							name="first_name"
							value={formData.first_name}
							onChange={handleChange}
							_placeholder={{ color: placeholderColor }}
						/>
						<Input
							placeholder="Last Name"
							name="last_name"
							value={formData.last_name}
							onChange={handleChange}
							_placeholder={{ color: placeholderColor }}
						/>
						<Input
							placeholder="Your Number"
							name="number"
							type="number"
							value={formData.number}
							onChange={handleChange}
							_placeholder={{ color: placeholderColor }}
						/>
					</Stack>
				);
			case 1:
				return (
					<Stack spacing={4}>
						<Input
							placeholder="Your Email"
							name="email"
							type="email"
							value={formData.email}
							onChange={handleChange}
							_placeholder={{ color: placeholderColor }}
						/>
						<Input
							placeholder="Password"
							name="password"
							type="password"
							value={formData.password}
							onChange={handleChange}
							_placeholder={{ color: placeholderColor }}
						/>
					</Stack>
				);
			case 2:
				return (
					<Stack spacing={6} align="center">
						<Stack direction="row" justify="center" spacing={6}>
							<Button
								colorScheme="blue"
								variant={["photographer", "videographer", "model", "studio"].includes(formData.user_type) ? "solid" : "outline"}
								onClick={() => {
									setFormData({ ...formData, user_type: '' });
									setIsVisualCreator(true); // 👈 NEW: visual creator selected
								}}
								bgGradient={["photographer", "videographer", "model", "studio"].includes(formData.user_type) ? "linear(to-r, #FF0080, #7928CA)" : undefined}
								textColor={["photographer", "videographer", "model", "studio"].includes(formData.user_type) ? "white" : "gray.600"}
							>
								Visual Creator
							</Button>
							<Button
								colorScheme="blue"
								variant={formData.user_type === "customer" ? "solid" : "outline"}
								onClick={() => {
									setFormData({ ...formData, user_type: "customer" });
									setIsVisualCreator(false); // 👈 NEW: visual creator *unselected*
								}}
								bgGradient={formData.user_type === "customer" ? "linear(to-r, #FF0080, #7928CA)" : undefined}
								textColor={formData.user_type === "customer" ? "white" : "gray.600"}
							>
								Customer
							</Button>
						</Stack>


						{/* Always show the creator type dropdown if not Customer */}
						{isVisualCreator && (
							<Box mt={4} width="100%" maxW="400px" animation="fadeIn 0.5s ease-in">
								<Select
									placeholder="Select Your Creator Type"
									options={[
										{ value: "photographer", label: "Photographer" },
										{ value: "videographer", label: "Videographer" },
										{ value: "model", label: "Model" },
										{ value: "studio", label: "Studio" },
									]}
									value={[
										{ value: "photographer", label: "Photographer" },
										{ value: "Videographer", label: "Videographer" },
										{ value: "model", label: "Model" },
										{ value: "studio", label: "Studio" },
									].find(option => option.value === formData.user_type)}
									onChange={(selectedOption) =>
										setFormData({ ...formData, user_type: selectedOption.value })
									}
									styles={{
										option: (base, state) => ({
											...base,
											color: "black",
											backgroundColor: state.isFocused ? 'gray.100' : 'transparent',
											"&:active": {
												backgroundColor: "#7928CA",
												color: "white",
											},
										}),
										singleValue: (base) => ({
											...base,
											color: "black",
										}),
									}}
								/>
							</Box>
						)}

						{(formData.user_type === 'photographer' || formData.user_type === 'videographer') && (
							<Input
								mt={4}
								placeholder="What Camera Are You Using? (e.g. Sony A7, Canon 5D)"
								name="camera"
								value={formData.camera}
								onChange={handleChange}
							/>
						)}


						{/* Photographer|VideoGrapher and Studio need Equipment */}
						{(formData.user_type === "photographer" || formData.user_type === "videographer" || formData.user_type === "studio") && (
							<Input
								placeholder="Enter your Equipment"
								name="equipment"
								value={formData.equipment}
								onChange={handleChange}
								mt={4}
							/>
						)}

						{/* Photographer and Model need Photography Styles */}
						{(formData.user_type === "photographer" || formData.user_type === "videographer" || formData.user_type === "model") && (
							<Box mt={4}>
								<Heading as="h3" size="sm" mb={2}>
									Your Styles
								</Heading>
								<Select
									isMulti
									closeMenuOnSelect={false}
									options={options}
									styles={{
										option: (base, state) => ({
											...base,
											color: dropdownTextColor,
											backgroundColor: state.isFocused ? 'gray.100' : 'transparent', // optional hover effect
											"&:active": {
												backgroundColor: "#7928CA",
												color: "white",
											},
										}),
										singleValue: (base) => ({
											...base,
											color: dropdownTextColor,
										}),
									}}
									value={options.filter(option =>
										formData.photography_style?.split(", ").includes(option.value)
									)}
									onChange={(selectedOptions) =>
										setFormData({
											...formData,
											photography_style: selectedOptions.map(option => option.value).join(", "),
										})
									}
								/>
							</Box>
						)}
					</Stack>




				);
			case 3: // Dynamic step for location
				return (
					<Stack spacing={4}>
						<Heading as="h3" size="md">
							{formData.user_type === "photographer" || "videographer"
								? "Where can clients find you?"
								: "Tell us about your location"}
						</Heading>
						<Text color="gray.500">
							{formData.user_type === "photographer" || "videographer"
								? "Let your clients know where you're based or the areas you cover."
								: "Help us personalize your experience by providing your location."}
						</Text>
						<Input
							placeholder="Street Address"
							name="street_address"
							value={formData.street_address || ""}
							onChange={handleChange}
							_placeholder={{ color: placeholderColor }}
						/>
						<Input
							placeholder="City"
							name="city"
							value={formData.city || ""}
							onChange={handleChange}
							_placeholder={{ color: placeholderColor }}
						/>
						<Input
							placeholder="State"
							name="state"
							value={formData.state || ""}
							onChange={handleChange}
							_placeholder={{ color: placeholderColor }}
						/>
						<Input
							placeholder="ZIP Code"
							name="zip"
							value={formData.zip || ""}
							onChange={handleChange}
							_placeholder={{ color: placeholderColor }}
						/>
					</Stack>
				);

			case 4:
				return (
					<Stack spacing={4} align="center">
						{/* Image Preview */}
						{formData.profilePicture && (
							<Avatar
								size="xl"
								src={URL.createObjectURL(formData.profilePicture)} // Dynamically create a preview URL
								alt="Profile Preview"
							/>
						)}
						<Input
							type="file"
							name="profilePicture"
							accept="image/jpeg, image/png, image/heif, image/heic"
							onChange={(e) => {
								const file = e.target.files[0];
								if (file) {
									const allowedTypes = ["image/jpeg", "image/png", "image/heif, image/heic"];
									if (allowedTypes.includes(file.type)) {
										setFormData({
											...formData,
											profilePicture: file,
											fileName: file.name,
											fileType: file.type
										});
									} else {
										alert("Please upload a JPEG, PNG, or HEIF file.");
										e.target.value = null;
									}
								}
							}}
						/>
						<Text>Upload your profile picture to complete your setup.</Text>
					</Stack>
				);
			default:
				return null;
		}
	};

	return (
		<Flex
			minH="70vh"
			align="center" // Ensures the box is vertically centered
			justify="center" // Ensures the box is horizontally centered
			bg={bgColor}
			maxW="100%"
		>
			<Box
				bg={boxColor}
				p={6}
				rounded="md"
				shadow="xl"
				borderColor="transparent"
				style={{
					outlineOffset: '4px', // Optional: Add space between border and outline
				}}
				w={{ base: '90%', sm: '80%', md: '400px' }}
				maxW="100%"
			>
				<Heading size="lg" mb={4} textAlign="center">
					{steps[currentStep].label}
				</Heading>
				<Text color="gray.500" mb={6} textAlign="center">
					{steps[currentStep].description}
				</Text>
				<Progress
					colorScheme="gray"
					value={(currentStep / (steps.length - 1)) * 100}
					mb={4}
					hasStripe
					isAnimated
				/>
				{renderStep()}
				<Flex justify="space-between" mt={6}>
					<Button
						isDisabled={currentStep === 0}
						onClick={handleBack}
						colorScheme="gray"
						_hover={{ transform: "scale(1.05)", boxShadow: "md" }}
						_active={{ transform: "scale(0.95)" }}
						transition="all 0.2s ease-in-out"
					>
						Back
					</Button>
					<Button
						onClick={currentStep === steps.length - 1 ? handleSubmit : handleNext}
						colorScheme="blue"
						bgGradient="linear(to-r, #FF0080, #7928CA)"
						textColor="white"
						_hover={{
							transform: "scale(1.05)",
							bgGradient: 'linear(to-r, red.500, purple.500)'
						}}
						_active={{ transform: "scale(0.95)" }}
						transition="all 0.2s ease-in-out"
						isDisabled={isNextDisabled() || isSubmitting}
						isLoading={isSubmitting}
						loadingText="Submitting..."
					>
						{currentStep === steps.length - 1 ? 'Finish' : 'Next'}
					</Button>

				</Flex>

			</Box>
		</Flex>
	);

};


export default Onboarding;


